import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Table, Card, Spinner, Alert, Form, Badge, Button, Modal, Toast, ToastContainer } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faSort, faSortUp, faSortDown, faEye, faDownload, faTrash, faCheck, faTimes, faMagic, faCode } from '@fortawesome/free-solid-svg-icons';
import { fetchTasks } from '../../redux/actions/taskActions';
import Pagination from '../common/Pagination';
import useDebounce from '../../hooks/useDebounce';
import ImageModal from '../common/ImageModal';
import RawJsonViewer from '../common/RawJsonViewer';
import axiosInstance from '../../utils/setupAxios';
import { adminGenerateSong } from '../../services/songAdminApi';
import {
  ListDesktop,
  ListMobile,
  MobileCard,
  MobileCardTop,
  MobileCardTitle,
  MobileCardRow,
  MobileCardMeta,
  MobileCardFooter,
} from '../common/ListMobileLayout';
import {
  getLanguageDisplayName,
  getLanguageFilterOptions,
  LANGUAGE_FILTER_UNKNOWN,
} from '../../utils/languageConstants';

// Status constants
const STATUS_OPEN = 'open';
const STATUS_IN_PROGRESS = 'in_progress';
const STATUS_DONE = 'done';
const STATUS_FAILED = 'failed';

const STEM_TASK_TYPES = ['separate_vocal', 'split_stem'];

const TYPE_LABELS = {
  separate_vocal: 'Separate Vocal',
  split_stem: 'Split Stem',
};

const STEM_LABELS = {
  vocal_url: 'Vocal',
  vocals_url: 'Vocals',
  instrumental_url: 'Instrumental',
  backing_vocals_url: 'Backing Vocals',
  drums_url: 'Drums',
  bass_url: 'Bass',
  guitar_url: 'Guitar',
  keyboard_url: 'Keyboard',
  percussion_url: 'Percussion',
  strings_url: 'Strings',
  synth_url: 'Synth',
  fx_url: 'FX',
  brass_url: 'Brass',
  woodwinds_url: 'Woodwinds',
  piano_url: 'Piano',
  other_url: 'Other',
};

const STEM_DISPLAY_ORDER = [
  'vocal_url',
  'vocals_url',
  'instrumental_url',
  'backing_vocals_url',
  'drums_url',
  'bass_url',
  'guitar_url',
  'keyboard_url',
  'percussion_url',
  'strings_url',
  'synth_url',
  'fx_url',
  'brass_url',
  'woodwinds_url',
  'piano_url',
  'other_url',
];

const TaskManager = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pendingOpenTaskId, setPendingOpenTaskId] = useState(() => {
    const tid = searchParams.get('task_id');
    const open = searchParams.get('open');
    if (tid && (open === '1' || open === 'true')) {
      return tid;
    }
    return null;
  });
  // Tracks that a fetch cycle started while a deep-link open is pending
  const sawLoadingForPendingOpenRef = useRef(false);
  // Last applied deep-link task_id/open — avoid resetting filters when only open is stripped
  const lastAppliedSearchParamsRef = useRef({
    taskId: searchParams.get('task_id'),
    open: (() => {
      const o = searchParams.get('open');
      return (o === '1' || o === 'true') ? o : null;
    })(),
  });
  
  // Initialize deviceId from localStorage with validation
  const getInitialDeviceId = () => {
    try {
      const savedDeviceId = localStorage.getItem('task_device_id') || '';
      // Validate length - if > 255, clear it
      if (savedDeviceId && savedDeviceId.length > 255) {
        localStorage.removeItem('task_device_id');
        return '';
      }
      return savedDeviceId;
    } catch (error) {
      console.error('Error loading device_id from localStorage:', error);
      return '';
    }
  };
  
  const [deviceId, setDeviceId] = useState(getInitialDeviceId());
  const [deviceIdError, setDeviceIdError] = useState('');
  const [taskIdFilter, setTaskIdFilter] = useState(() => searchParams.get('task_id') || '');
  const debouncedTaskIdFilter = useDebounce(taskIdFilter, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [languageFilter, setLanguageFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [rawTask, setRawTask] = useState(null);
  const [rawData, setRawData] = useState(null);
  const [rawLoading, setRawLoading] = useState(false);
  const [rawError, setRawError] = useState(null);
  const [deletingTaskId, setDeletingTaskId] = useState(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success'); // 'success' or 'error'

  // Generate Song modal state
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateStep, setGenerateStep] = useState('input'); // input | generating | done
  const [generateForm, setGenerateForm] = useState({ type: 'prompt', prompt: '', lyrics: '', title: '', style: '', gender: '', genres: [], moods: [], make_instrumental: false });
  const [genreInput, setGenreInput] = useState('');
  const [moodInput, setMoodInput] = useState('');
  const [genreDropdown, setGenreDropdown] = useState(false);
  const [moodDropdown, setMoodDropdown] = useState(false);
  const [systemGenres, setSystemGenres] = useState([]);
  const [systemMoods, setSystemMoods] = useState([]);
  const [generateResult, setGenerateResult] = useState(null);
  const [generateError, setGenerateError] = useState(null);
  const [generateLoading, setGenerateLoading] = useState(false);

  // Debounce the search query with a 500ms delay
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const { items: tasks, loading, total, count, error } = useSelector((state) => ({
    items: state.tasks.items || [],
    loading: state.tasks.loading,
    total: state.tasks.total || 0,
    count: state.tasks.count || 0,
    error: state.tasks.error
  }));

  // Validate device_id on change
  useEffect(() => {
    if (deviceId && deviceId.length > 255) {
      setDeviceIdError('Device ID must not be greater than 255 characters');
      // Clear from localStorage immediately
      localStorage.removeItem('task_device_id');
    } else {
      setDeviceIdError('');
    }
  }, [deviceId]);

  useEffect(() => {
    // Don't fetch if there's a validation error
    if (deviceIdError) {
      return;
    }

    const params = {
      page: currentPage,
      items_per_page: pageSize
    };

    // device_id is optional - only add if provided and valid
    if (deviceId && deviceId.length <= 255) {
      params.device_id = deviceId;
    }

    if (debouncedSearchQuery) {
      params.search = debouncedSearchQuery;
    }

    if (statusFilter !== 'all') {
      params.status = statusFilter;
    }

    if (serviceFilter !== 'all') {
      params.service = serviceFilter;
    }

    if (typeFilter !== 'all') {
      params.type = typeFilter;
    }

    if (languageFilter !== 'all') {
      params.content_language = languageFilter;
    }

    if (sortBy) {
      params.sort_by = sortBy;
    }

    if (sortOrder) {
      params.sort_order = sortOrder;
    }

    if (dateFrom) {
      params.date_from = dateFrom;
    }

    if (dateTo) {
      params.date_to = dateTo;
    }

    if (debouncedTaskIdFilter.trim()) {
      params.task_id = debouncedTaskIdFilter.trim();
    }

    dispatch(fetchTasks(params));
  }, [dispatch, deviceId, deviceIdError, currentPage, pageSize, debouncedSearchQuery, statusFilter, serviceFilter, typeFilter, languageFilter, sortBy, sortOrder, dateFrom, dateTo, debouncedTaskIdFilter]);

  // Deep link: apply task_id / open from URL only when they meaningfully change
  useEffect(() => {
    const tid = searchParams.get('task_id');
    const openRaw = searchParams.get('open');
    const open = (openRaw === '1' || openRaw === 'true') ? openRaw : null;

    const last = lastAppliedSearchParamsRef.current;
    const tidChanged = tid !== last.taskId;
    const openChanged = open !== last.open;

    if (!tidChanged && !openChanged) {
      return;
    }

    // Only open was removed and task_id is unchanged — do not reset page/filter
    if (tid && !tidChanged && !open && last.open) {
      lastAppliedSearchParamsRef.current = { taskId: tid, open: null };
      return;
    }

    if (tid && tidChanged) {
      setTaskIdFilter(tid);
      setCurrentPage(1);
    }
    if (tid && open && (openChanged || tidChanged)) {
      setPendingOpenTaskId(tid);
    }

    lastAppliedSearchParamsRef.current = { taskId: tid, open };
  }, [searchParams]);

  const handleTaskIdFilterChange = (e) => {
    setTaskIdFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleDeviceIdChange = (e) => {
    const value = e.target.value;
    setDeviceId(value);
    // Only save to localStorage if valid
    if (value && value.length <= 255) {
      localStorage.setItem('task_device_id', value);
    } else if (!value) {
      localStorage.removeItem('task_device_id');
    }
    setCurrentPage(1); // Reset to first page when device ID changes
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'status':
        setStatusFilter(value);
        break;
      case 'service':
        setServiceFilter(value);
        break;
      case 'type':
        setTypeFilter(value);
        break;
      case 'language':
        setLanguageFilter(value);
        break;
      default:
        break;
    }
    setCurrentPage(1);
  };

  const handleDateChange = (type, value) => {
    if (type === 'from') {
      setDateFrom(value);
    } else {
      setDateTo(value);
    }
    setCurrentPage(1);
  };

  const normalizeStatus = (status, service) => {
    const statusMap = {
      [STATUS_OPEN]: STATUS_OPEN,
      [STATUS_IN_PROGRESS]: STATUS_IN_PROGRESS,
      [STATUS_DONE]: STATUS_DONE,
      [STATUS_FAILED]: STATUS_FAILED,
      'IN_QUEUE': STATUS_OPEN,
      'IN_PROCESSING': STATUS_IN_PROGRESS,
      'COMPLETED': STATUS_DONE,
      'FAILED': STATUS_FAILED
    };
    return statusMap[status] || status;
  };

  const getStatusBadgeVariant = (normalizedStatus) => {
    const variants = {
      [STATUS_OPEN]: 'warning',
      [STATUS_IN_PROGRESS]: 'info',
      [STATUS_DONE]: 'success',
      [STATUS_FAILED]: 'danger'
    };
    return variants[normalizedStatus] || 'secondary';
  };

  const formatDate = (timestamp) => {
    if (!timestamp || timestamp === 0) return 'N/A';
    return new Date(timestamp * 1000).toLocaleString();
  };

  const parseJsonField = (field) => {
    try {
      return typeof field === 'string' ? JSON.parse(field) : field;
    } catch {
      return field;
    }
  };

  const isStemTask = (task) => STEM_TASK_TYPES.includes(task?.type);

  const getTypeLabel = (type) => TYPE_LABELS[type] || type || '—';

  const getStemData = (resultData, inputData, taskType) => {
    const parsed = parseJsonField(resultData) || {};
    const input = parseJsonField(inputData) || {};
    const stems = parsed?.stems && typeof parsed.stems === 'object' ? parsed.stems : {};

    return {
      stems,
      separationType: parsed.separation_type || input.separation_type || taskType,
      provider: parsed.provider || input.provider || '',
      sourceTaskId: input.source_task_id || null,
      audioIndex: input.audio_index ?? null,
      sourceService: input.source_service || null,
    };
  };

  const getOrderedStemEntries = (stems) => {
    const entries = Object.entries(stems).filter(([, url]) => typeof url === 'string' && url.trim() !== '');
    const orderIndex = (key) => {
      const index = STEM_DISPLAY_ORDER.indexOf(key);
      return index === -1 ? STEM_DISPLAY_ORDER.length : index;
    };

    return entries.sort(([a], [b]) => orderIndex(a) - orderIndex(b));
  };

  const getTaskTitle = (task) => {
    if (task.title && task.title.trim() !== '') return task.title;
    const inputData = parseJsonField(task.input_data);
    if (inputData && inputData.title && inputData.title.trim() !== '') return inputData.title;
    const resultData = parseJsonField(task.result);
    if (resultData && resultData.name && resultData.name.trim() !== '') return resultData.name;
    return 'N/A';
  };

  const renderLanguageLabel = (code) => {
    if (!code) {
      return <span className="text-muted">—</span>;
    }
    return (
      <>
        {getLanguageDisplayName(code)}
        <span className="text-muted ms-1">({code})</span>
      </>
    );
  };

  const handleViewTask = (task) => {
    setSelectedTask(task);
  };

  const handleCloseTaskModal = () => {
    setSelectedTask(null);
  };

  // Deep link: open detail modal after filtered fetch completes
  useEffect(() => {
    if (pendingOpenTaskId && loading) {
      sawLoadingForPendingOpenRef.current = true;
    }
  }, [pendingOpenTaskId, loading]);

  useEffect(() => {
    if (!pendingOpenTaskId) return;

    if (deviceIdError) {
      setPendingOpenTaskId(null);
      sawLoadingForPendingOpenRef.current = false;
      setToastMessage('Could not open task: Device ID validation is blocking task loading');
      setToastType('error');
      setShowToast(true);
      const next = new URLSearchParams(searchParams);
      next.delete('open');
      setSearchParams(next, { replace: true });
      return;
    }

    if (loading) return;
    // Wait until debounced filter matches so we do not open/toast on a stale list
    if (debouncedTaskIdFilter.trim() !== pendingOpenTaskId) return;
    // Wait until at least one fetch has started (avoids toast on initial empty store)
    if (!sawLoadingForPendingOpenRef.current) return;

    const match = (tasks || []).find((t) => t.task_id === pendingOpenTaskId);
    if (match) {
      handleViewTask(match);
      setPendingOpenTaskId(null);
      sawLoadingForPendingOpenRef.current = false;
      const next = new URLSearchParams(searchParams);
      next.delete('open');
      setSearchParams(next, { replace: true });
    } else {
      setPendingOpenTaskId(null);
      sawLoadingForPendingOpenRef.current = false;
      setToastMessage(`Task not found: ${pendingOpenTaskId}`);
      setToastType('error');
      setShowToast(true);
      const next = new URLSearchParams(searchParams);
      next.delete('open');
      setSearchParams(next, { replace: true });
    }
  }, [pendingOpenTaskId, deviceIdError, loading, tasks, debouncedTaskIdFilter, searchParams, setSearchParams]);

  // View raw database fields (input_data, result_data, etc.) for a task
  const handleViewRaw = async (task) => {
    setRawTask(task);
    setRawData(null);
    setRawError(null);
    setRawLoading(true);
    try {
      const response = await axiosInstance.get('/songs/admin/songs/raw', {
        params: { task_id: task.task_id },
      });
      setRawData(response.data?.data ?? null);
    } catch (err) {
      setRawError(err.response?.data?.message || err.message || 'Failed to load raw data');
    } finally {
      setRawLoading(false);
    }
  };

  const handleCloseRawModal = () => {
    setRawTask(null);
    setRawData(null);
    setRawError(null);
  };

  // Show toast notification
  const showToastMessage = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  // Handle delete button click - show confirmation modal
  const handleDeleteSong = (taskId, taskTitle) => {
    if (!taskId) {
      showToastMessage('Task ID not found', 'error');
      return;
    }
    setTaskToDelete({ taskId, taskTitle });
    setShowDeleteConfirmModal(true);
  };

  // Handle confirmation - proceed with deletion
  const handleConfirmDelete = async () => {
    if (!taskToDelete) return;

    const { taskId } = taskToDelete;
    setDeletingTaskId(taskId);
    setShowDeleteConfirmModal(false);

    try {
      await axiosInstance.delete('/songs/delete-by-task-id', {
        data: { task_id: taskId }
      });
      
      // Refresh tasks list with current filters
      const params = {
        page: currentPage,
        items_per_page: pageSize
      };

      if (deviceId && deviceId.length <= 255) {
        params.device_id = deviceId;
      }

      if (debouncedSearchQuery) {
        params.search = debouncedSearchQuery;
      }

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      if (serviceFilter !== 'all') {
        params.service = serviceFilter;
      }

      if (typeFilter !== 'all') {
        params.type = typeFilter;
      }

      if (languageFilter !== 'all') {
        params.content_language = languageFilter;
      }

      if (sortBy) {
        params.sort_by = sortBy;
      }

      if (sortOrder) {
        params.sort_order = sortOrder;
      }

      if (dateFrom) {
        params.date_from = dateFrom;
      }

      if (dateTo) {
        params.date_to = dateTo;
      }

      if (debouncedTaskIdFilter.trim()) {
        params.task_id = debouncedTaskIdFilter.trim();
      }

      dispatch(fetchTasks(params));
      
      // Show success toast
      showToastMessage('Song request deleted successfully', 'success');
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete song request';
      showToastMessage(`Error: ${errorMessage}`, 'error');
    } finally {
      setDeletingTaskId(null);
      setTaskToDelete(null);
    }
  };

  // Handle cancel - close confirmation modal
  const handleCancelDelete = () => {
    setShowDeleteConfirmModal(false);
    setTaskToDelete(null);
  };

  const getInputAudioUrl = (task) => {
    const inputData = parseJsonField(task.input_data);
    if (inputData?.conversions && Array.isArray(inputData.conversions) && inputData.conversions.length > 0) {
      const conversion = inputData.conversions[0];
      return conversion.audio_url || conversion.audio_url_wav || '';
    }
    return inputData?.audio_file || inputData?.audioUrl || '';
  };

  const getOutputAudioUrl = (task) => {
    let resultData = parseJsonField(task.result);
    // Handle MusicGPT nested JSON structure
    if (task.service === 'musicgpt' && resultData?.result && typeof resultData.result === 'string') {
      try {
        const nestedResult = JSON.parse(resultData.result);
        resultData = { ...resultData, ...nestedResult };
      } catch (e) {
        // Keep original resultData
      }
    }
    if (resultData?.result_url) return resultData.result_url;
    if (resultData?.audio_url) return resultData.audio_url;
    if (resultData?.conversions && Array.isArray(resultData.conversions) && resultData.conversions.length > 0) {
      const conversion = resultData.conversions[0];
      return conversion.audio_url || conversion.audio_url_wav || '';
    }
    return '';
  };

  const getThumbnailUrl = (task) => {
    const resultData = parseJsonField(task.result);
    // Handle MusicGPT nested JSON structure
    if (task.service === 'musicgpt' && resultData?.result && typeof resultData.result === 'string') {
      try {
        const nestedResult = JSON.parse(resultData.result);
        resultData = { ...resultData, ...nestedResult };
      } catch (e) {
        // Keep original resultData
      }
    }
    return resultData?.thumbnail_url || '';
  };

  const handleOpenGenerateModal = async () => {
    setGenerateStep('input');
    setGenerateForm({ type: 'prompt', prompt: '', lyrics: '', title: '', style: '', gender: '', genres: [], moods: [], make_instrumental: false });
    setGenreInput('');
    setMoodInput('');
    setGenreDropdown(false);
    setMoodDropdown(false);
    setGenerateResult(null);
    setGenerateError(null);
    setShowGenerateModal(true);
    try {
      const [genreRes, moodRes] = await Promise.all([
        axiosInstance.get('/songs/settings/genre/list'),
        axiosInstance.get('/songs/settings/mood/list'),
      ]);
      setSystemGenres(genreRes.data?.data || []);
      setSystemMoods(moodRes.data?.data || []);
    } catch (_) {}
  };

  const handleCloseGenerateModal = () => {
    setShowGenerateModal(false);
  };

  const toggleTag = (field, value) => {
    setGenerateForm((f) => {
      const current = f[field];
      return { ...f, [field]: current.includes(value) ? current.filter((v) => v !== value) : [...current, value] };
    });
  };

  const addManualTag = (field, input, setInput) => {
    const tags = input.split(',').map((t) => t.trim()).filter(Boolean);
    if (!tags.length) return;
    setGenerateForm((f) => ({ ...f, [field]: [...new Set([...f[field], ...tags])] }));
    setInput('');
  };

  const removeTag = (field, value) => {
    setGenerateForm((f) => ({ ...f, [field]: f[field].filter((v) => v !== value) }));
  };

  const handleGenerateSubmit = async (e) => {
    e.preventDefault();
    const { type, prompt, lyrics, title, style, gender, genres, moods, make_instrumental } = generateForm;
    if (type === 'prompt' && !prompt.trim()) return;
    if (type === 'lyrics' && !lyrics.trim()) return;

    setGenerateLoading(true);
    setGenerateError(null);
    setGenerateStep('generating');

    const styleParts = [...genres, ...moods, style.trim(), make_instrumental ? 'Instrumental' : ''].filter(Boolean);
    const mergedStyle = styleParts.join(', ');

    const payload = { type };
    if (type === 'prompt') payload.prompt = prompt.trim();
    else payload.lyrics = lyrics.trim();
    if (mergedStyle) payload.style = mergedStyle;
    if (title.trim()) payload.title = title.trim();
    if (gender.trim()) payload.gender = gender.trim();

    try {
      const res = await adminGenerateSong(payload);
      setGenerateResult(res.data?.data || res.data);
      setGenerateStep('done');
    } catch (err) {
      setGenerateError(err.response?.data?.message || err.message || 'Generation failed');
      setGenerateStep('done');
    } finally {
      setGenerateLoading(false);
    }
  };

  const renderGenerateModal = () => (
    <Modal show={showGenerateModal} onHide={handleCloseGenerateModal} size="lg" centered scrollable fullscreen="md-down">
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faMagic} className="me-2" />
          Generate Song
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {generateStep === 'input' && (
          <Form onSubmit={handleGenerateSubmit}>
            {/* Type */}
            <Form.Group className="mb-3">
              <Form.Label>Type <span className="text-danger">*</span></Form.Label>
              <div className="d-flex gap-3">
                <Form.Check
                  type="radio"
                  label="Prompt"
                  id="gen-type-prompt"
                  checked={generateForm.type === 'prompt'}
                  onChange={() => setGenerateForm((f) => ({ ...f, type: 'prompt' }))}
                />
                <Form.Check
                  type="radio"
                  label="Lyrics"
                  id="gen-type-lyrics"
                  checked={generateForm.type === 'lyrics'}
                  onChange={() => setGenerateForm((f) => ({ ...f, type: 'lyrics' }))}
                />
              </div>
            </Form.Group>

            {/* Prompt or Lyrics */}
            {generateForm.type === 'prompt' ? (
              <Form.Group className="mb-3">
                <Form.Label>Prompt <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  className="form-control"
                  value={generateForm.prompt}
                  onChange={(e) => setGenerateForm((f) => ({ ...f, prompt: e.target.value }))}
                  placeholder="Describe the song you want to generate..."
                  required
                />
              </Form.Group>
            ) : (
              <Form.Group className="mb-3">
                <Form.Label>Lyrics <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={6}
                  className="form-control"
                  value={generateForm.lyrics}
                  onChange={(e) => setGenerateForm((f) => ({ ...f, lyrics: e.target.value }))}
                  placeholder="Enter full song lyrics..."
                  required
                />
              </Form.Group>
            )}

            {/* Genres */}
            <Form.Group className="mb-3">
              <Form.Label>Genres <span className="text-muted">(optional)</span></Form.Label>
              {generateForm.genres.length > 0 && (
                <div className="d-flex flex-wrap gap-1 mb-2">
                  {generateForm.genres.map((g) => (
                    <Badge key={g} bg="primary" style={{ cursor: 'pointer', color: '#fff' }} onClick={() => removeTag('genres', g)}>
                      {g} ×
                    </Badge>
                  ))}
                </div>
              )}
              <div style={{ position: 'relative' }}>
                <Form.Control
                  type="text"
                  size="sm"
                  className="form-control"
                  value={genreInput}
                  onChange={(e) => { setGenreInput(e.target.value); setGenreDropdown(true); }}
                  onFocus={() => setGenreDropdown(true)}
                  onBlur={() => setTimeout(() => setGenreDropdown(false), 150)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addManualTag('genres', genreInput, setGenreInput); setGenreDropdown(false); } }}
                  placeholder="Type or select genre..."
                />
                {genreDropdown && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, background: '#fff', border: '1px solid #dee2e6', borderRadius: 4, maxHeight: 180, overflowY: 'auto' }}>
                    {systemGenres
                      .filter((g) => !generateForm.genres.includes(g) && (!genreInput.trim() || g.toLowerCase().includes(genreInput.toLowerCase())))
                      .map((g) => (
                        <div
                          key={g}
                          style={{ padding: '6px 12px', cursor: 'pointer' }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
                          onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                          onMouseDown={() => { toggleTag('genres', g); setGenreInput(''); setGenreDropdown(false); }}
                        >
                          {g}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </Form.Group>

            {/* Moods */}
            <Form.Group className="mb-3">
              <Form.Label>Moods <span className="text-muted">(optional)</span></Form.Label>
              {generateForm.moods.length > 0 && (
                <div className="d-flex flex-wrap gap-1 mb-2">
                  {generateForm.moods.map((m) => (
                    <Badge key={m} bg="success" style={{ cursor: 'pointer', color: '#fff' }} onClick={() => removeTag('moods', m)}>
                      {m} ×
                    </Badge>
                  ))}
                </div>
              )}
              <div style={{ position: 'relative' }}>
                <Form.Control
                  type="text"
                  size="sm"
                  className="form-control"
                  value={moodInput}
                  onChange={(e) => { setMoodInput(e.target.value); setMoodDropdown(true); }}
                  onFocus={() => setMoodDropdown(true)}
                  onBlur={() => setTimeout(() => setMoodDropdown(false), 150)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addManualTag('moods', moodInput, setMoodInput); setMoodDropdown(false); } }}
                  placeholder="Type or select mood..."
                />
                {moodDropdown && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, background: '#fff', border: '1px solid #dee2e6', borderRadius: 4, maxHeight: 180, overflowY: 'auto' }}>
                    {systemMoods
                      .filter((m) => !generateForm.moods.includes(m) && (!moodInput.trim() || m.toLowerCase().includes(moodInput.toLowerCase())))
                      .map((m) => (
                        <div
                          key={m}
                          style={{ padding: '6px 12px', cursor: 'pointer' }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
                          onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                          onMouseDown={() => { toggleTag('moods', m); setMoodInput(''); setMoodDropdown(false); }}
                        >
                          {m}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </Form.Group>

            {/* Custom Style */}
            <Form.Group className="mb-3">
              <Form.Label>Custom Style <span className="text-muted">(optional)</span></Form.Label>
              <Form.Control
                type="text"
                className="form-control"
                value={generateForm.style}
                onChange={(e) => setGenerateForm((f) => ({ ...f, style: e.target.value }))}
                placeholder="e.g. acoustic, lo-fi, cinematic..."
              />
            </Form.Group>

            {/* Title + Gender + Make Instrumental */}
            <div className="row">
              <div className="col-md-5">
                <Form.Group className="mb-3">
                  <Form.Label>Title <span className="text-muted">(optional)</span></Form.Label>
                  <Form.Control
                    type="text"
                    className="form-control"
                    value={generateForm.title}
                    onChange={(e) => setGenerateForm((f) => ({ ...f, title: e.target.value }))}
                    placeholder="Song title..."
                  />
                </Form.Group>
              </div>
              <div className="col-md-3">
                <Form.Group className="mb-3">
                  <Form.Label>Gender <span className="text-muted">(optional)</span></Form.Label>
                  <Form.Select
                    className="form-control"
                    value={generateForm.gender}
                    onChange={(e) => setGenerateForm((f) => ({ ...f, gender: e.target.value }))}
                  >
                    <option value="">— Any —</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="neutral">Neutral</option>
                  </Form.Select>
                </Form.Group>
              </div>
              {generateForm.type === 'prompt' && (
                <div className="col-md-4">
                  <Form.Group className="mb-3 pt-4 mt-1">
                    <Form.Check
                      type="checkbox"
                      label="Make Instrumental (no vocals)"
                      checked={generateForm.make_instrumental}
                      onChange={(e) => setGenerateForm((f) => ({ ...f, make_instrumental: e.target.checked }))}
                    />
                  </Form.Group>
                </div>
              )}
            </div>

            <div className="d-flex gap-2 justify-content-end">
              <Button variant="secondary" type="button" onClick={handleCloseGenerateModal}>
                <FontAwesomeIcon icon={faTimes} className="me-1" />Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={
                  (generateForm.type === 'prompt' && !generateForm.prompt.trim()) ||
                  (generateForm.type === 'lyrics' && !generateForm.lyrics.trim())
                }
              >
                <FontAwesomeIcon icon={faMagic} className="me-1" />Generate
              </Button>
            </div>
          </Form>
        )}

        {generateStep === 'generating' && (
          <div className="text-center py-4">
            <Spinner animation="border" className="mb-3" />
            <p className="mb-0">Submitting generation request...</p>
          </div>
        )}

        {generateStep === 'done' && (
          <div>
            {generateError ? (
              <Alert variant="danger">
                <strong>Error:</strong> {generateError}
              </Alert>
            ) : (
              <Alert variant="success">
                <strong>Generation started!</strong>
                {generateResult?.task_id && (
                  <div className="mt-2">
                    Task ID: <code>{generateResult.task_id}</code>
                  </div>
                )}
              </Alert>
            )}
            <div className="d-flex gap-2 justify-content-end mt-3">
              <Button variant="secondary" onClick={handleCloseGenerateModal}>Close</Button>
              {!generateError && (
                <Button variant="primary" onClick={() => { handleCloseGenerateModal(); dispatch(fetchTasks({ page: 1, items_per_page: pageSize })); }}>
                  <FontAwesomeIcon icon={faCheck} className="me-1" />Refresh Tasks
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );

  const exportToCSV = () => {
    if (tasks.length === 0) {
      alert('No data to export');
      return;
    }

    // Prepare CSV data
    const headers = ['task_id', 'device_id', 'service', 'status', 'title', 'audio_url_input', 'audio_url_output', 'thumbnail_url'];
    const rows = tasks.map(task => {
      const normalizedStatus = normalizeStatus(task.status, task.service);
      return [
        task.task_id || '',
        task.device_id || '',
        task.service || '',
        normalizedStatus,
        getTaskTitle(task),
        getInputAudioUrl(task),
        getOutputAudioUrl(task),
        getThumbnailUrl(task)
      ];
    });

    // Convert to CSV format
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => {
        // Escape commas and quotes in cell values
        const cellValue = String(cell || '').replace(/"/g, '""');
        return `"${cellValue}"`;
      }).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    
    // Generate filename with current date/time
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    link.setAttribute('download', `tasks_export_${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderSortIcon = (field) => {
    if (sortBy !== field) {
      return <FontAwesomeIcon icon={faSort} className="ms-1 text-muted" />;
    }
    return sortOrder === 'asc' 
      ? <FontAwesomeIcon icon={faSortUp} className="ms-1" />
      : <FontAwesomeIcon icon={faSortDown} className="ms-1" />;
  };

  const renderJsonFields = (data, title) => {
    if (!data || typeof data !== 'object') {
      return (
        <div className="bg-light p-3 rounded border text-muted">
          No {title.toLowerCase()} available
        </div>
      );
    }

    const formatFieldName = (key) => {
      return key
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    };

    const renderFieldValue = (value) => {
      if (value === null) return <span className="text-muted">null</span>;
      if (value === undefined) return <span className="text-muted">undefined</span>;
      if (typeof value === 'boolean') return <span className="text-primary">{value.toString()}</span>;
      if (typeof value === 'number') return <span className="text-info">{value}</span>;
      if (typeof value === 'string') {
        // Check if it's a URL
        if (value.startsWith('http://') || value.startsWith('https://')) {
          return (
            <a href={value} target="_blank" rel="noopener noreferrer" className="text-break">
              {value}
            </a>
          );
        }
        // Check if it's a long text (like lyrics)
        if (value.length > 100) {
          return (
            <div 
              className="bg-white p-2 rounded border text-break" 
              style={{ 
                maxHeight: '200px', 
                overflow: 'auto',
                fontSize: '0.85rem',
                whiteSpace: 'pre-wrap'
              }}
            >
              {value}
            </div>
          );
        }
        return <span className="text-break">{value}</span>;
      }
      if (Array.isArray(value)) {
        return (
          <div className="bg-white p-2 rounded border">
            {value.map((item, idx) => (
              <div key={idx} className="mb-1">
                {typeof item === 'object' ? JSON.stringify(item, null, 2) : String(item)}
              </div>
            ))}
          </div>
        );
      }
      if (typeof value === 'object') {
        return (
          <div className="bg-white p-2 rounded border">
            {Object.entries(value).map(([k, v]) => (
              <div key={k} className="mb-1">
                <strong>{formatFieldName(k)}:</strong> {renderFieldValue(v)}
              </div>
            ))}
          </div>
        );
      }
      return <span>{String(value)}</span>;
    };

    return (
      <div className="bg-light p-3 rounded border" style={{ maxHeight: '500px', overflow: 'auto' }}>
        {Object.entries(data).map(([key, value]) => (
          <div key={key} className="mb-3 pb-3 border-bottom">
            <div className="fw-bold text-muted mb-1" style={{ fontSize: '0.85rem' }}>
              {formatFieldName(key)}:
            </div>
            <div style={{ fontSize: '0.9rem' }}>
              {renderFieldValue(value)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderMusicGPTDetails = (task, resultData) => {
    const formatDuration = (seconds) => {
      if (!seconds) return 'N/A';
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const DetailRow = ({ label, value, isUrl = false, isLongText = false }) => {
      if (!value && value !== 0) return null;
      
      return (
        <div className="mb-3 pb-3 border-bottom">
          <div className="fw-bold text-muted mb-2" style={{ fontSize: '0.9rem' }}>
            {label}:
          </div>
          <div style={{ fontSize: '0.95rem' }}>
            {isUrl ? (
              <a href={value} target="_blank" rel="noopener noreferrer" className="text-break text-primary">
                {value}
              </a>
            ) : isLongText ? (
              <div 
                className="bg-white p-3 rounded border text-break" 
                style={{ 
                  maxHeight: '300px', 
                  overflow: 'auto',
                  fontSize: '0.9rem',
                  whiteSpace: 'pre-wrap',
                  lineHeight: '1.6'
                }}
              >
                {value}
              </div>
            ) : (
              <span className="text-break">{value}</span>
            )}
          </div>
        </div>
      );
    };

    return (
      <div className="row">
        <div className="col-md-12">
          <h5 className="mb-4 pb-2 border-bottom">Result Data</h5>
          <div className="bg-light p-4 rounded" style={{ maxHeight: '600px', overflowY: 'auto' }}>
            <DetailRow label="Name" value={resultData?.name || task.title || 'N/A'} />
            <DetailRow label="Lyrics" value={resultData?.lyrics} isLongText={true} />
            <DetailRow label="Thumbnail Url" value={resultData?.thumbnail_url || task.thumbnail_url} isUrl={true} />
            <DetailRow label="Result url" value={resultData?.result_url || task.share_url} isUrl={true} />
            <DetailRow label="Duration" value={resultData?.duration ? formatDuration(resultData.duration) : (task.duration ? formatDuration(parseFloat(task.duration)) : 'N/A')} />
            {resultData?.create_time && (
              <DetailRow label="Create time" value={new Date(resultData.create_time * 1000).toLocaleString()} />
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderStemDetails = (task, resultData, inputData) => {
    const stemData = getStemData(resultData, inputData, task.type);
    const stemEntries = getOrderedStemEntries(stemData.stems);

    const InfoRow = ({ label, value }) => {
      if (value === null || value === undefined || value === '') return null;
      return (
        <div className="mb-3 pb-3 border-bottom">
          <div className="fw-bold text-muted mb-2" style={{ fontSize: '0.9rem' }}>
            {label}:
          </div>
          <div style={{ fontSize: '0.95rem' }} className="text-break">
            {value}
          </div>
        </div>
      );
    };

    return (
      <div className="row">
        <div className="col-md-12">
          <h5 className="mb-4 pb-2 border-bottom">Stem Separation</h5>
          <div className="bg-light p-4 rounded mb-4">
            <InfoRow label="Separation Type" value={getTypeLabel(stemData.separationType)} />
            <InfoRow label="Provider" value={stemData.provider || '—'} />
            <InfoRow label="Source Task ID" value={stemData.sourceTaskId} />
            <InfoRow label="Source Service" value={stemData.sourceService} />
            {stemData.audioIndex !== null && stemData.audioIndex !== undefined && (
              <InfoRow label="Audio Index" value={String(stemData.audioIndex)} />
            )}
          </div>

          <h6 className="mb-3">Separated Stems</h6>
          {stemEntries.length === 0 ? (
            <div className="bg-light p-3 rounded border text-muted">
              No stem audio available yet
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {stemEntries.map(([stemKey, stemUrl]) => (
                <div key={stemKey} className="bg-light p-3 rounded border">
                  <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                    <span className="fw-semibold">{STEM_LABELS[stemKey] || stemKey}</span>
                    <a href={stemUrl} target="_blank" rel="noopener noreferrer" className="small text-break">
                      Open URL
                    </a>
                  </div>
                  <div className="bg-dark p-3 rounded">
                    <audio controls style={{ width: '100%' }} preload="metadata">
                      <source src={stemUrl} type="audio/mpeg" />
                      <source src={stemUrl} type="audio/mp3" />
                      <source src={stemUrl} type="audio/wav" />
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Suno tasks it could not finish are handed over (stems to ApiHub, add-vocals to MusicGPT);
  // input_data keeps why.
  const getFailoverInfo = (task) => {
    const input = parseJsonField(task?.input_data);
    if (!input?.failover_from) return null;

    return {
      from: input.failover_from,
      to: input.failover_to || input.provider || 'apihub',
      failed: Boolean(input.musicgpt_error),
      reason: input.failover_reason || '',
      message: input.failover_message || input.failover_error?.error_message || '',
      errorCode: input.failover_error?.error_code ?? null,
      at: input.apihub_failover_at || input.musicgpt_failover_at || '',
      apiHubRequestId: input.apihub_request_id || '',
      sunoTaskId: input.suno_task_id || '',
    };
  };

  const renderFailoverBadge = (task) => {
    const failover = getFailoverInfo(task);
    if (!failover) return null;

    return (
      <Badge
        bg={failover.failed ? 'danger' : 'warning'}
        className={failover.failed ? 'text-white' : 'text-dark'}
        title={failover.message || `${failover.from} -> ${failover.to}`}
      >
        {failover.from} → {failover.to}{failover.failed ? ' ✗' : ''}
      </Badge>
    );
  };

  // Provider failures are stored in result_data (error_code / error_message / error_type).
  // A stem job that was handed over to another provider records that in input_data.
  const renderErrorDetails = (resultData, inputData) => {
    const errorCode = resultData?.error_code ?? resultData?.errorCode ?? null;
    const errorMessage = resultData?.error_message || resultData?.errorMessage || resultData?.msg || '';
    const errorType = resultData?.error_type || resultData?.errorType || '';
    const failoverFrom = inputData?.failover_from || '';
    const failoverError = inputData?.failover_error || null;
    const failoverMessage = inputData?.failover_message || failoverError?.error_message || '';

    const failoverTo = inputData?.failover_to || inputData?.provider || 'apihub';
    // Set when the provider we failed over to also failed (vocal jobs handed to MusicGPT).
    const musicGptError = inputData?.musicgpt_error || null;

    if (!errorCode && !errorMessage && !errorType && !failoverFrom) return null;

    return (
      <div className="row mb-3">
        <div className="col-md-12">
          {(errorCode || errorMessage || errorType) && (
            <Alert variant="danger" className="mb-2">
              <div className="fw-semibold mb-1">
                Provider error{errorCode ? ` (code ${errorCode})` : ''}
                {errorType ? ` - ${errorType}` : ''}
              </div>
              <div className="text-break">{errorMessage || 'No error message returned by the provider'}</div>
            </Alert>
          )}
          {failoverFrom && (
            <Alert variant="warning" className="mb-0">
              <div className="fw-semibold mb-1">
                Failed over from {failoverFrom} to {failoverTo}
                {(inputData?.apihub_failover_at || inputData?.musicgpt_failover_at)
                  ? ` at ${inputData.apihub_failover_at || inputData.musicgpt_failover_at}`
                  : ''}
              </div>
              {failoverMessage && <div className="text-break">{failoverMessage}</div>}
              {inputData?.failover_reason && (
                <div className="small text-muted mt-1">Reason: {inputData.failover_reason}</div>
              )}
              {inputData?.suno_task_id && (
                <div className="small text-muted">Suno task ID: {inputData.suno_task_id}</div>
              )}
              {inputData?.apihub_request_id && (
                <div className="small text-muted">ApiHub request ID: {inputData.apihub_request_id}</div>
              )}
              {inputData?.musicgpt_task_id && (
                <div className="small text-muted">MusicGPT task ID: {inputData.musicgpt_task_id}</div>
              )}
            </Alert>
          )}
          {musicGptError && (
            <Alert variant="danger" className="mt-2 mb-0">
              <div className="fw-semibold mb-1">
                MusicGPT also failed
                {musicGptError.stage === 'submit' ? ' - refused the submission' : ' - generation failed'}
                {musicGptError.status_code ? ` (HTTP ${musicGptError.status_code})` : ''}
                {musicGptError.error_code ? ` - ${musicGptError.error_code}` : ''}
              </div>
              <div className="text-break">{musicGptError.error_message || 'No error message returned by MusicGPT'}</div>
              {musicGptError.at && <div className="small text-muted mt-1">At: {musicGptError.at}</div>}
              {musicGptError.provider_response && (
                <details className="mt-1">
                  <summary className="small">Raw MusicGPT response</summary>
                  <pre className="small mb-0 text-break" style={{ whiteSpace: 'pre-wrap' }}>
                    {typeof musicGptError.provider_response === 'string'
                      ? musicGptError.provider_response
                      : JSON.stringify(musicGptError.provider_response, null, 2)}
                  </pre>
                </details>
              )}
            </Alert>
          )}
        </div>
      </div>
    );
  };

  const renderTaskDetailsModal = () => {
    if (!selectedTask) return null;

    const inputData = parseJsonField(selectedTask.input_data);
    let resultData = parseJsonField(selectedTask.result);
    const normalizedStatus = normalizeStatus(selectedTask.status, selectedTask.service);

    // Handle MusicGPT nested JSON structure
    // For MusicGPT, result.result might be a JSON string that needs parsing
    if (selectedTask.service === 'musicgpt' && resultData?.result && typeof resultData.result === 'string') {
      try {
        const nestedResult = JSON.parse(resultData.result);
        // Merge nested result into resultData
        resultData = { ...resultData, ...nestedResult };
      } catch (e) {
        // If parsing fails, keep original resultData
        console.warn('Failed to parse nested result JSON:', e);
      }
    }

    // Check if this is a MusicGPT task (by type or service)
    const isMusicGPT = selectedTask.type === 'musicgpt' || selectedTask.service === 'musicgpt';
    const isStemSeparation = isStemTask(selectedTask);

    // Extract audio URLs from input data
    const getInputAudioUrl = () => {
      // Check conversions array for MusicGPT
      if (inputData?.conversions && Array.isArray(inputData.conversions) && inputData.conversions.length > 0) {
        const conversion = inputData.conversions[0];
        if (conversion.audio_url) return conversion.audio_url;
        if (conversion.audio_url_wav) return conversion.audio_url_wav;
      }
      // Check direct fields
      if (inputData?.audio_file) return inputData.audio_file;
      if (inputData?.audioUrl) return inputData.audioUrl;
      return null;
    };

    // Extract audio URLs from output data
    const getOutputAudioUrl = () => {
      // Check result_url first (works for MusicGPT after parsing nested JSON)
      if (resultData?.result_url) return resultData.result_url;
      if (resultData?.audio_url) return resultData.audio_url;
      // Check conversions array in output data
      if (resultData?.conversions && Array.isArray(resultData.conversions) && resultData.conversions.length > 0) {
        const conversion = resultData.conversions[0];
        if (conversion.audio_url) return conversion.audio_url;
        if (conversion.audio_url_wav) return conversion.audio_url_wav;
      }
      return null;
    };

    const inputAudioUrl = getInputAudioUrl();
    const outputAudioUrl = getOutputAudioUrl();

    return (
      <Modal show={!!selectedTask} onHide={handleCloseTaskModal} size="xl" centered scrollable fullscreen="md-down">
        <Modal.Header closeButton>
          <Modal.Title>Task Details - {selectedTask.task_id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row mb-3">
            <div className="col-md-12">
              <div className="d-flex gap-3 flex-wrap align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted">Status:</span>
                  <Badge 
                    bg={getStatusBadgeVariant(normalizedStatus)}
                    className={['danger', 'secondary', 'success'].includes(getStatusBadgeVariant(normalizedStatus)) ? 'text-white' : 'text-dark'}
                    style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                  >
                    {normalizedStatus}
                  </Badge>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted">Service:</span>
                  <Badge 
                    bg="warning"
                    text="dark"
                    style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                  >
                    {selectedTask.service}
                  </Badge>
                </div>
                {selectedTask.type && (
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-muted">Type:</span>
                    <Badge 
                      bg={isStemSeparation ? 'primary' : 'secondary'}
                      className="text-white"
                      style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                    >
                      {getTypeLabel(selectedTask.type)}
                    </Badge>
                  </div>
                )}
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted">Language:</span>
                  <Badge
                    bg="light"
                    text="dark"
                    style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                  >
                    {selectedTask.content_language
                      ? `${getLanguageDisplayName(selectedTask.content_language)} (${selectedTask.content_language})`
                      : 'Unknown'}
                  </Badge>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted">Created:</span>
                  <Badge 
                    bg="info"
                    style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                  >
                    {formatDate(selectedTask.create_time)}
                  </Badge>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted">Updated:</span>
                  <Badge 
                    bg="info"
                    style={{ padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                  >
                    {formatDate(selectedTask.update_time)}
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {renderErrorDetails(resultData, inputData)}

          {isStemSeparation ? (
            <>
              {renderStemDetails(selectedTask, resultData, inputData)}

              <div className="row mt-4">
                <div className="col-md-6">
                  <h6 className="mb-3">Input Data</h6>
                  {renderJsonFields(inputData, 'Input Data')}
                </div>
                <div className="col-md-6">
                  <h6 className="mb-3">Raw Result Data</h6>
                  {resultData ? (
                    renderJsonFields(resultData, 'Output Data')
                  ) : (
                    <div className="bg-light p-3 rounded border text-muted">
                      No result data available
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : isMusicGPT ? (
            <>
              {renderMusicGPTDetails(selectedTask, resultData)}
              
              {/* Output Audio Player */}
              {outputAudioUrl && (
                <div className="row mt-4">
                  <div className="col-md-12">
                    <h6 className="mb-2">Output Audio Player:</h6>
                    <div className="bg-dark p-3 rounded">
                      <audio 
                        controls 
                        style={{ width: '100%' }}
                        preload="metadata"
                      >
                        <source src={outputAudioUrl} type="audio/mpeg" />
                        <source src={outputAudioUrl} type="audio/mp3" />
                        <source src={outputAudioUrl} type="audio/wav" />
                        <source src={outputAudioUrl} type="audio/m4a" />
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                  </div>
                </div>
              )}

              {/* Thumbnail Section */}
              {(resultData?.thumbnail_url || selectedTask.thumbnail_url) && (
                <div className="row mt-4">
                  <div className="col-md-12">
                    <h6 className="mb-2">Thumbnail:</h6>
                    <img 
                      src={resultData?.thumbnail_url || selectedTask.thumbnail_url} 
                      alt="Thumbnail" 
                      style={{ maxWidth: '100%', maxHeight: '400px', cursor: 'pointer', borderRadius: '8px' }}
                      onClick={() => {
                        setSelectedImage(resultData?.thumbnail_url || selectedTask.thumbnail_url);
                        handleCloseTaskModal();
                      }}
                      className="img-thumbnail"
                    />
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="row">
              <div className="col-md-6">
                <h6 className="mb-3">Input Data:</h6>
                {renderJsonFields(inputData, 'Input Data')}
                
                {/* Input Audio Player */}
                {inputAudioUrl && (
                  <div className="mt-3">
                    <h6 className="mb-2">Input Audio Player:</h6>
                    <div className="bg-dark p-3 rounded">
                      <audio 
                        controls 
                        style={{ width: '100%' }}
                        preload="metadata"
                      >
                        <source src={inputAudioUrl} type="audio/mpeg" />
                        <source src={inputAudioUrl} type="audio/mp3" />
                        <source src={inputAudioUrl} type="audio/wav" />
                        <source src={inputAudioUrl} type="audio/m4a" />
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                  </div>
                )}
              </div>
              <div className="col-md-6">
                <h6 className="mb-3">Output Data:</h6>
                {resultData ? (
                  <div>
                    {renderJsonFields(resultData, 'Output Data')}
                    
                    {/* Output Audio Player */}
                    {outputAudioUrl && (
                      <div className="mt-3">
                        <h6 className="mb-2">Output Audio Player:</h6>
                        <div className="bg-dark p-3 rounded">
                          <audio 
                            controls 
                            style={{ width: '100%' }}
                            preload="metadata"
                          >
                            <source src={outputAudioUrl} type="audio/mpeg" />
                            <source src={outputAudioUrl} type="audio/mp3" />
                            <source src={outputAudioUrl} type="audio/wav" />
                            <source src={outputAudioUrl} type="audio/m4a" />
                            Your browser does not support the audio element.
                          </audio>
                        </div>
                      </div>
                    )}
                    
                    {resultData.thumbnail_url && (
                      <div className="mt-3">
                        <h6 className="mb-2">Thumbnail:</h6>
                        <img 
                          src={resultData.thumbnail_url} 
                          alt="Thumbnail" 
                          style={{ maxWidth: '100%', cursor: 'pointer' }}
                          onClick={() => {
                            setSelectedImage(resultData.thumbnail_url);
                            handleCloseTaskModal();
                          }}
                          className="img-thumbnail"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-light p-3 rounded border text-muted">
                    No result data available
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseTaskModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };

  // Check if error is about device_id validation and handle it
  useEffect(() => {
    if (error) {
      const errorMessage = typeof error === 'string' ? error : (error.message || String(error));
      const errorLower = errorMessage.toLowerCase();
      if (errorLower.includes('device id') && errorLower.includes('255')) {
        setDeviceIdError('Device ID must not be greater than 255 characters');
        // Reset device_id filter
        setDeviceId('');
        localStorage.removeItem('task_device_id');
      }
    } else {
      // Clear deviceIdError when error is cleared (if it was a device_id error)
      if (deviceIdError && deviceIdError.includes('255')) {
        setDeviceIdError('');
      }
    }
  }, [error, deviceIdError]);

  return (
    <div className="dashboard-list-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Task Manager</h2>
        <div className="d-flex gap-2">
          <Button variant="primary" onClick={handleOpenGenerateModal}>
            <FontAwesomeIcon icon={faMagic} className="me-2" />
            Generate Song
          </Button>
          <Button
            variant="success"
            onClick={exportToCSV}
            disabled={tasks.length === 0 || loading}
          >
            <FontAwesomeIcon icon={faDownload} className="me-2" />
            Export to CSV
          </Button>
        </div>
      </div>

      {/* Error Alert - only show if it's not a device_id validation error */}
      {error && (() => {
        const errorMessage = typeof error === 'string' ? error : (error.message || String(error));
        const errorLower = errorMessage.toLowerCase();
        // Don't show alert for device_id validation errors (they're shown inline)
        if (errorLower.includes('device id') && errorLower.includes('255')) {
          return null;
        }
        return (
          <Alert variant="danger" className="mb-3" dismissible>
            {errorMessage}
          </Alert>
        );
      })()}

      <Card>
        <Card.Body>
          {/* Device ID and Task ID filters */}
          <div className="row mb-4 g-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Device ID (Optional Filter)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Filter by device ID - leave empty to show all"
                  value={deviceId}
                  onChange={handleDeviceIdChange}
                  isInvalid={!!deviceIdError}
                  maxLength={255}
                />
                {deviceIdError ? (
                  <Form.Text className="text-danger d-block">
                    {deviceIdError}
                  </Form.Text>
                ) : (
                  <Form.Text className="text-muted">
                    {deviceId.length}/255 characters
                  </Form.Text>
                )}
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Task ID (Optional Filter)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter exact task ID to look up a single task"
                  value={taskIdFilter}
                  onChange={handleTaskIdFilterChange}
                />
                <Form.Text className="text-muted">
                  Fetch a specific task directly — much faster than browsing all tasks.
                </Form.Text>
              </Form.Group>
            </div>
          </div>

          {/* Filters */}
          <div className="d-flex flex-wrap gap-3 mb-4">
            <div className="flex-grow-1" style={{ minWidth: '200px' }}>
              <div className="input-group">
                <span className="input-group-text">
                  <FontAwesomeIcon icon={faSearch} />
                </span>
                <Form.Control
                  type="text"
                  placeholder="Search by title, task_id, or device_id..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </div>
            </div>
                <div style={{ minWidth: '150px' }}>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value={STATUS_OPEN}>Open</option>
                    <option value={STATUS_IN_PROGRESS}>In Progress</option>
                    <option value={STATUS_DONE}>Done</option>
                    <option value={STATUS_FAILED}>Failed</option>
                  </Form.Select>
                </div>
                <div style={{ minWidth: '150px' }}>
                  <Form.Select
                    value={serviceFilter}
                    onChange={(e) => handleFilterChange('service', e.target.value)}
                  >
                    <option value="all">All Services</option>
                    <option value="suno">Suno</option>
                    <option value="apihub">API Hub</option>
                    <option value="musicgpt">MusicGPT</option>
                    <option value="kie">KIE</option>
                  </Form.Select>
                </div>
                <div style={{ minWidth: '150px' }}>
                  <Form.Select
                    value={typeFilter}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                  >
                    <option value="all">All Types</option>
                    <option value="prompt">Prompt</option>
                    <option value="lyrics">Lyrics</option>
                    <option value="cover">Cover</option>
                    <option value="lipsync">Lipsync</option>
                    <option value="voice_model">Voice Model</option>
                    <option value="extend_lyrics">Extend Lyrics</option>
                    <option value="extend_prompt">Extend Prompt</option>
                    <option value="cover_prompt">Cover Prompt</option>
                    <option value="cover_lyrics">Cover Lyrics</option>
                    <option value="music_ai">Music AI</option>
                    <option value="musicgpt">MusicGPT</option>
                    <option value="sound_generator">Sound Generator</option>
                    <option value="sing_over_instrumental">Sing Over Instrumental</option>
                    <option value="separate_vocal">Separate Vocal</option>
                    <option value="split_stem">Split Stem</option>
                  </Form.Select>
                </div>
                <div style={{ minWidth: '150px' }}>
                  <Form.Select
                    value={languageFilter}
                    onChange={(e) => handleFilterChange('language', e.target.value)}
                  >
                    <option value="all">All Languages</option>
                    <option value={LANGUAGE_FILTER_UNKNOWN}>Unknown</option>
                    {getLanguageFilterOptions().map((code) => (
                      <option key={code} value={code}>
                        {getLanguageDisplayName(code)} ({code})
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </div>

          {/* Date Range Filters */}
          <div className="d-flex gap-3 mb-4">
            <div style={{ minWidth: '200px' }}>
              <Form.Label>Date From</Form.Label>
              <Form.Control
                type="date"
                value={dateFrom}
                onChange={(e) => handleDateChange('from', e.target.value)}
              />
            </div>
            <div style={{ minWidth: '200px' }}>
              <Form.Label>Date To</Form.Label>
              <Form.Control
                type="date"
                value={dateTo}
                onChange={(e) => handleDateChange('to', e.target.value)}
              />
            </div>
          </div>

          {/* Table */}
          <div className="list-pagination-top">
            <Pagination
              currentPage={currentPage}
              totalItems={total}
              itemsPerPage={pageSize}
              onPageChange={handlePageChange}
              position="top"
            />
          </div>

          <ListDesktop>
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('task_id')}
                  >
                    Task ID {renderSortIcon('task_id')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('device_id')}
                  >
                    Device ID {renderSortIcon('device_id')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('service')}
                  >
                    Service {renderSortIcon('service')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('type')}
                  >
                    Type {renderSortIcon('type')}
                  </th>
                  <th
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('content_language')}
                  >
                    Language {renderSortIcon('content_language')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('status')}
                  >
                    Status {renderSortIcon('status')}
                  </th>
                  <th>Title</th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('created_at')}
                  >
                    Created {renderSortIcon('created_at')}
                  </th>
                  <th 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('updated_at')}
                  >
                    Updated {renderSortIcon('updated_at')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" className="text-center">
                      <div className="loading-spinner">
                        <Spinner animation="border" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </Spinner>
                      </div>
                    </td>
                  </tr>
                ) : tasks.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center text-muted">
                      No tasks found
                    </td>
                  </tr>
                ) : (
                  tasks.map((task) => {
                    const normalizedStatus = normalizeStatus(task.status, task.service);
                    const resultData = parseJsonField(task.result);
                    
                    return (
                      <tr key={task.task_id}>
                        <td>{task.task_id}</td>
                        <td>{task.device_id || <span className="text-muted">N/A</span>}</td>
                        <td>
                          <div className="d-flex flex-column gap-1 align-items-start">
                            <Badge bg="warning" text="dark">{task.service}</Badge>
                            {renderFailoverBadge(task)}
                          </div>
                        </td>
                        <td>
                          <Badge
                            bg={isStemTask(task) ? 'primary' : 'secondary'}
                            className="text-white"
                            style={{ padding: '0.35rem 0.65rem', fontSize: '0.875rem' }}
                          >
                            {getTypeLabel(task.type)}
                          </Badge>
                        </td>
                        <td>{renderLanguageLabel(task.content_language)}</td>
                        <td>
                          <Badge 
                            bg={getStatusBadgeVariant(normalizedStatus)}
                            className={['danger', 'secondary', 'success'].includes(getStatusBadgeVariant(normalizedStatus)) ? 'text-white' : 'text-dark'}
                          >
                            {normalizedStatus}
                          </Badge>
                        </td>
                        <td>{getTaskTitle(task)}</td>
                        <td>{formatDate(task.create_time)}</td>
                        <td>{formatDate(task.update_time)}</td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() => handleViewTask(task)}
                              title="View Details"
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleViewRaw(task)}
                              title="View Raw Data"
                            >
                              <FontAwesomeIcon icon={faCode} />
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteSong(task.task_id, getTaskTitle(task))}
                              disabled={deletingTaskId === task.task_id}
                              title="Delete song request"
                            >
                              {deletingTaskId === task.task_id ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <FontAwesomeIcon icon={faTrash} />
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                    )}
                  </tbody>
                </Table>
          </ListDesktop>

          {!loading && tasks.length > 0 && (
            <ListMobile>
              {tasks.map((task) => {
                const normalizedStatus = normalizeStatus(task.status, task.service);
                return (
                  <MobileCard key={task.task_id}>
                    <MobileCardTop>
                      <Badge bg="warning" text="dark">{task.service}</Badge>
                      {renderFailoverBadge(task)}
                      <Badge
                        bg={getStatusBadgeVariant(normalizedStatus)}
                        className={['danger', 'secondary', 'success'].includes(getStatusBadgeVariant(normalizedStatus)) ? 'text-white' : 'text-dark'}
                      >
                        {normalizedStatus}
                      </Badge>
                    </MobileCardTop>
                    <MobileCardTitle>{getTaskTitle(task)}</MobileCardTitle>
                    <MobileCardMeta>{formatDate(task.create_time)}</MobileCardMeta>
                    <MobileCardRow label="Task ID" mono>
                      {task.task_id}
                    </MobileCardRow>
                    <MobileCardRow label="Device ID" mono>
                      {task.device_id || 'N/A'}
                    </MobileCardRow>
                    <MobileCardRow label="Type">{getTypeLabel(task.type)}</MobileCardRow>
                    <MobileCardRow label="Language">
                      {task.content_language
                        ? `${getLanguageDisplayName(task.content_language)} (${task.content_language})`
                        : '—'}
                    </MobileCardRow>
                    <MobileCardRow label="Updated">{formatDate(task.update_time)}</MobileCardRow>
                    <MobileCardFooter>
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => handleViewTask(task)}
                        title="View Details"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => handleViewRaw(task)}
                        title="View Raw Data"
                      >
                        <FontAwesomeIcon icon={faCode} />
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteSong(task.task_id, getTaskTitle(task))}
                        disabled={deletingTaskId === task.task_id}
                        title="Delete song request"
                      >
                        {deletingTaskId === task.task_id ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <FontAwesomeIcon icon={faTrash} />
                        )}
                      </Button>
                    </MobileCardFooter>
                  </MobileCard>
                );
              })}
            </ListMobile>
          )}

          <Pagination
            currentPage={currentPage}
            totalItems={total}
            itemsPerPage={pageSize}
            onPageChange={handlePageChange}
            position="bottom"
          />
        </Card.Body>
      </Card>

      {/* Task Details Modal */}
      {renderTaskDetailsModal()}

      {/* Raw Data Modal */}
      <Modal show={!!rawTask} onHide={handleCloseRawModal} size="xl" centered scrollable fullscreen="md-down">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faCode} className="me-2" />
            Raw Data{rawTask ? ` - ${rawTask.task_id}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {rawLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" className="mb-3" />
              <p className="mb-0">Loading raw data...</p>
            </div>
          ) : rawError ? (
            <Alert variant="danger" className="mb-0">
              {rawError}
            </Alert>
          ) : rawData ? (
            <RawJsonViewer data={rawData} maxHeight="65vh" />
          ) : (
            <div className="bg-light p-3 rounded border text-muted">No raw data available</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseRawModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Generate Song Modal */}
      {renderGenerateModal()}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirmModal} onHide={handleCancelDelete} centered scrollable fullscreen="sm-down">
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this song request?</p>
          {taskToDelete && (
            <div className="mb-3">
              <p className="mb-1"><strong>Task:</strong> {taskToDelete.taskTitle || 'Unknown'}</p>
              <p className="mb-0"><strong>Task ID:</strong> {taskToDelete.taskId}</p>
            </div>
          )}
          <p className="text-danger mb-0"><strong>This action cannot be undone.</strong></p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelDelete}>
            <FontAwesomeIcon icon={faTimes} className="me-2" />
            No
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            <FontAwesomeIcon icon={faCheck} className="me-2" />
            Yes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Toast Notification */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={5000}
          autohide
          bg={toastType === 'success' ? 'success' : 'danger'}
        >
          <Toast.Header closeButton>
            <strong className="me-auto">
              {toastType === 'success' ? 'Success' : 'Error'}
            </strong>
          </Toast.Header>
          <Toast.Body className={toastType === 'error' ? 'text-white' : ''}>
            {toastMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Image Modal */}
      {selectedImage && (
        <ImageModal
          show={!!selectedImage}
          onHide={() => setSelectedImage(null)}
          imageUrl={selectedImage}
        />
      )}
    </div>
  );
};

export default TaskManager;

